import { Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC, useEffect } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, FORM_FIELDS_TYPES } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import { checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import PruefAssistenz from '../../../../../../antrag-components/PruefAssistenz';
import { InhaltTrenner } from '../../../../../../antrag-components/Wrapper';
import { checkIfPruefAssitenzNeedsToValidate } from '../../../../../../application-helper-function/AppHelperfunctions';

// import Schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const ErfolgskontrolleAntragStartupProjekte: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	useEffect(() => {
		const qualitativePath = P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Qualitative_Ergebnisse.path;
		const quantitativePath = P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Quantitative_Ergebnisse.path;

		const qualitativeValue = get(application, qualitativePath);
		const quantitativeValue = get(application, quantitativePath);

		if (!qualitativeValue || (Array.isArray(qualitativeValue) && qualitativeValue.length === 0)) {
			setApplicationProperty(qualitativePath, [{}]);
		}
		if (!quantitativeValue || (Array.isArray(quantitativeValue) && quantitativeValue.length === 0)) {
			setApplicationProperty(quantitativePath, [{}]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Typography variant='h3' component='h1' sx={{ mt: "1rem", mb: 0 }}>
				Darstellung der Effektivität und Effizienz der Maßnahmen
			</Typography>
			<Typography variant='body2' sx={{ mb: 1 }}>
				Gemeint sind qualitative Ziele (wie z.B. Motivation, Qualität, Kundenzufriedenheit, Sichtbarkeit) sowie mess-
				oder zählbare Leistungskennzahlen/Erfolgsparameter wie z.B. Teilnehmer, Begünstigte oder Veranstaltungen.
			</Typography>

			<PruefAssistenz
				statusPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Pruefstatus.path}
				hinweisPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Ueberarbeitet_Am.path}
				erledigtPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Erledigt.path}
				formStruc={formStrucPruefAssistenz()}
			/>
			<Typography variant='h5' sx={{ mb: 1, mt: '1rem' }}>
				a.) Angestrebte qualitative Ergebnisse
			</Typography>
			<CreateForm
				formStruc={formStrucQualitative()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner dotted marginTop='3rem' marginBottom='3rem' />
			<Typography variant='h5' sx={{ mb: 1 }}>
				b.) Angestrebte quantitative Ergebnisse
			</Typography>
			<CreateForm
				formStruc={formStrucQuantitative()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner dotted marginTop='3rem' marginBottom='3rem' />
			<Typography variant='h5' sx={{ mb: 0 }}>
				Projektdokumentation
			</Typography>
			<Typography variant='body2' sx={{ mb: 0 }}>
				(u.a. Wie werden Projekterfolge und Ergebnisse nachhaltig dokumentiert und ggf. weiterverwendet?)
			</Typography>
			<CreateForm
				formStruc={formStrucField2()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default ErfolgskontrolleAntragStartupProjekte;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucQualitative = (): FormStrucItem[] => {
	return [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Qualitative_Ergebnisse,
					type: FORM_FIELDS_TYPES.componentTable,
					bulletpoint: 'bullet',
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const formStrucQuantitative = (): FormStrucItem[] => {
	return [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Quantitative_Ergebnisse,
					type: FORM_FIELDS_TYPES.componentTable,
					fieldProps: {
						Erfolgsparameter: { tooltip: 'z.B. Ausgewählte Start-up-Unternehmen' },
						Anzahl_Wert: { tooltip: 'z.B. 10' },
					},
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const formStrucField1 = (): FormStrucItem[] => {
	return [...formStrucQualitative(), ...formStrucQuantitative()];
};
const formStrucField2 = (): FormStrucItem[] => {
	return [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0046783012_iert_Und_Ggf_Weiterverwendet,
					hideLabel: true,
					type: FORM_FIELDS_TYPES.richtext,
					maxCharLength: 3000,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};

export const getFormStrucErfolgskontrolleAntragStartupProjekte = (application: any) => {
	if (
		checkIfPruefAssitenzNeedsToValidate(
			application,
			P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Pruefstatus.path,
			P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Erfolgskontrolle_Erledigt.path,
		)
	)
		return concat(formStrucPruefAssistenz(), formStrucField1(), formStrucField2());
	return concat(formStrucField1(), formStrucField2());
};
